/**
 * The gate shown when no fluidvip session is present.
 *
 * Mirrors FluidTalk and FluidGhost: Google OAuth plus email/password against the
 * SHARED fluidvip Supabase, with the Turnstile challenge GoTrue enforces. It is
 * the same account everywhere, so account creation lives on talk. and this
 * screen only signs in.
 *
 * Most visitors never see this: the session cookie is scoped to .fluidvip.com,
 * so anyone already signed in elsewhere in the ecosystem arrives authenticated.
 */
import { useState, type FormEvent } from 'react';
import { useAuth } from '../../lib/auth';
import { TURNSTILE_SITE_KEY, SIGNUP_URL } from '../../lib/env';
import BrandMark, { Wordmark } from '../BrandMark';
import Turnstile from './Turnstile';

/** Google's brand mark. Drawn here because icon sets don't carry brand logos. */
function GoogleIcon() {
  return (
    <svg viewBox="0 0 18 18" width="16" height="16" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

export default function LoginScreen() {
  const { signInWithPassword, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);

  const captchaRequired = TURNSTILE_SITE_KEY.length > 0;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (captchaRequired && !captchaToken) {
      setError('Complete the verification challenge first.');
      return;
    }
    setBusy(true);
    const res = await signInWithPassword(email.trim(), password, captchaToken ?? undefined);
    if (res.error) {
      // A used Turnstile token can't be replayed — get a fresh one for the retry.
      setCaptchaToken(null);
      setCaptchaKey((k) => k + 1);
      setError(res.error);
      setBusy(false);
    }
    // On success the auth-state change swaps this screen out; stay busy.
  }

  async function onGoogle() {
    setError(null);
    setBusy(true);
    const res = await signInWithGoogle();
    if (res.error) {
      setError(res.error);
      setBusy(false);
    }
  }

  return (
    <div style={{
      minHeight: '100%', display: 'grid', placeItems: 'center', padding: 24,
      background: 'radial-gradient(80% 60% at 50% 0%, rgba(239,113,52,.07), transparent 70%), var(--fd-bg)',
    }}>
      <div style={{ width: 'min(380px, 100%)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', marginBottom: 8 }}>
          <BrandMark size={34} />
          <Wordmark size={24} />
        </div>
        <p className="faint" style={{ textAlign: 'center', fontSize: 12.5, margin: '0 0 26px' }}>
          Sign in with your Fluid account.
        </p>

        <div className="card">
          <button className="btn" onClick={onGoogle} disabled={busy} style={{ width: '100%', padding: 10 }}>
            <GoogleIcon /> Continue with Google
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0' }}>
            <span style={{ flex: 1, height: 1, background: 'var(--fd-hair)' }} />
            <span className="eyebrow">or</span>
            <span style={{ flex: 1, height: 1, background: 'var(--fd-hair)' }} />
          </div>

          <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
            <label style={{ display: 'block' }}>
              <span className="eyebrow">Email</span>
              <input
                className="field" type="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                style={{ marginTop: 6 }} placeholder="you@example.com"
              />
            </label>
            <label style={{ display: 'block' }}>
              <span className="eyebrow">Password</span>
              <input
                className="field" type="password" autoComplete="current-password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                style={{ marginTop: 6 }}
              />
            </label>

            {captchaRequired && (
              <Turnstile siteKey={TURNSTILE_SITE_KEY} onToken={setCaptchaToken} resetKey={captchaKey} />
            )}

            {error && <div className="notice bad" role="alert">{error}</div>}

            <button className="btn btn-primary" type="submit" disabled={busy} style={{ padding: 10 }}>
              {busy ? <><span className="spin" /> Signing in…</> : 'Sign in'}
            </button>
          </form>
        </div>

        <p className="faint" style={{ textAlign: 'center', fontSize: 12, marginTop: 18, lineHeight: 1.6 }}>
          One account covers every Fluid app.{' '}
          <a href={SIGNUP_URL} style={{ color: 'var(--fd-primary-soft)' }}>Create one</a>{' '}
          if you haven't yet.
        </p>
      </div>
    </div>
  );
}
